import { create } from 'zustand';
import Collection from '@model-core/core/Collection';
import UserModel from '@/models/entities/UserModel';
import { useAuthStore } from '@/stores/useAuthStore';

/** URN d'un utilisateur, tel que l'API l'enregistre dans createdBy / updatedBy ($:teiji:user:12) */
const USER_URN = /^\$:teiji:user:(\d+)$/;

interface UserDirectory {
  /**
   * Nom complet (« DOE Jane ») par auteur : createdBy / updatedBy de l'API contiennent l'URN de l'utilisateur
   * connecté ($:teiji:user:12) ; les données plus anciennes, son email
   */
  names: Record<string, string>;
  /**
   * Demande le nom de ces auteurs : les demandes d'un même rendu sont groupées (GET /users?id[]=… pour les URN,
   * ?email[]=… pour les emails), chaque auteur n'étant demandé qu'une fois. Résolue quand les noms sont chargés.
   */
  request: (authors: string[]) => Promise<void>;
}

/** Auteurs déjà demandés (trouvés ou non) et en attente d'envoi */
const requested = new Set<string>();
let pending: string[] = [];
let flush: Promise<void> | null = null;

export const useUserDirectory = create<UserDirectory>(() => ({
  names: {},

  request: (authors) => {
    const missing = authors.filter((author) => author && !requested.has(author));
    missing.forEach((author) => requested.add(author));
    pending.push(...missing);

    // Envoi au tour suivant de la boucle d'événements : regroupe les composants affichés ensemble
    flush ??= new Promise<void>((resolve) => setTimeout(resolve, 0)).then(async () => {
      const batch = pending;
      pending = [];
      flush = null;
      if (!batch.length) return;
      const ids = batch.flatMap((author) => USER_URN.exec(author)?.[1] ?? []);
      const emails = batch.filter((author) => !USER_URN.test(author));
      try {
        const [byId, byEmail] = await Promise.all([
          ids.length ? new Collection(UserModel).setFilter('id', ids as never).list() : null,
          emails.length ? new Collection(UserModel).setFilter('email', emails as never).list() : null,
        ]);
        const found = {
          ...Object.fromEntries(
            (byId?.collection.models ?? []).map((user) => [`$:teiji:user:${user.id}`, user.fullName]),
          ),
          ...Object.fromEntries(
            (byEmail?.collection.models ?? [])
              .filter((user) => emails.includes(user.email))
              .map((user) => [user.email, user.fullName]),
          ),
        };
        useUserDirectory.setState((state) => ({ names: { ...state.names, ...found } }));
      } catch {
        // Annuaire indisponible : les auteurs restent affichés sans leur nom
      }
    });
    return flush;
  },
}));

/**
 * Libellé d'un auteur : « le système » sans auteur (fixtures, migration, import, consumer), son nom s'il est connu,
 * sinon « un utilisateur inconnu » pour une URN (utilisateur supprimé) ou l'email ; suivi de « (moi) » pour
 * l'utilisateur connecté (par son URN, ou son email pour les données anciennes)
 */
export function useAuthorLabel(author: string | null | undefined): string {
  const name = useUserDirectory((state) => (author ? state.names[author] : undefined));
  const isMe = useAuthStore((state) => !!author && (author === state.user?.urn || author === state.user?.email));
  if (!author) return 'le système';
  const label = name ?? (USER_URN.test(author) ? 'un utilisateur inconnu' : author);
  return isMe ? `${label} (moi)` : label;
}
